// src/features/system/orchestrator/components/WorkersV2Panel.tsx
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MinimalisticTable, { ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import ConfirmDeleteButton from '@/shared/components/ui/ConfirmDeleteButton';
import { timeAgo } from '@/utils/date';
import { useNow } from '@/utils/useNow';
import { fetchWorkersV2Api, Worker } from '@/features/system/orchestrator/api/workersApi';
import { triggerWorkflowV2Api } from '@/features/system/orchestrator/api/workflowsV2Api';
import CreateWorkerDialog from '@/features/system/orchestrator/components/CreateWorkerDialog';

const QUERY_KEY = ['admin', 'orchestrator', 'workers'] as const;

function formatDate(val: string) {
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toISOString();
}

type WorkerRow = Worker;

// Avoid direct import.meta access — CRA/webpack treats it as a Critical dependency
// warning, which fails CI builds (CI=true → warnings are errors).
const META_ENV: Record<string, string | undefined> =
  (typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>) : {});

const DELETE_WORKER_WORKFLOW_KEY =
  META_ENV.VITE_ORCHESTRATOR_DELETE_WORKER_WORKFLOW_KEY
  || META_ENV.REACT_APP_ORCHESTRATOR_DELETE_WORKER_WORKFLOW_KEY
  || META_ENV.ORCHESTRATOR_DELETE_WORKER_WORKFLOW_KEY
  || 'system.worker.soft_delete';

const DELETE_WORKER_WORKFLOW_VERSION = (() => {
  const raw =
    META_ENV.VITE_ORCHESTRATOR_DELETE_WORKER_WORKFLOW_VERSION
    || META_ENV.REACT_APP_ORCHESTRATOR_DELETE_WORKER_WORKFLOW_VERSION
    || META_ENV.ORCHESTRATOR_DELETE_WORKER_WORKFLOW_VERSION;
  if (raw == null || String(raw).trim() === '') return 1;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 1;
})();

export default function WorkersV2Panel() {
  const qc = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  // Keep the “Created” relative time live (e.g. 17 min 2s ago → 17 min 3s ago).
  const now = useNow(1000);

  const { data, isLoading, error } = useQuery<{ items: WorkerRow[] }, Error>({
    queryKey: [...QUERY_KEY, 'v2'],
    queryFn: async () => {
      const res = await fetchWorkersV2Api({ limit: 200, registry_fallback: true });
      return { items: res.items ?? [] };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ worker_key }: { worker_key: string }) => {
      await triggerWorkflowV2Api({
        workflow_key: DELETE_WORKER_WORKFLOW_KEY,
        version: DELETE_WORKER_WORKFLOW_VERSION,
        inputs: { worker_key },
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [...QUERY_KEY, 'v2'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workers'] });
    },
  });

  const workersForTable = useMemo(() => {
    const items = (data?.items ?? []).slice();
    items.sort((a, b) => {
      const ak = String(a?.worker_key ?? '').toLocaleLowerCase();
      const bk = String(b?.worker_key ?? '').toLocaleLowerCase();
      return ak.localeCompare(bk);
    });
    return items;
  }, [data]);

  const columns: ColumnDef<WorkerRow>[] = useMemo(() => [
    { header: 'Key', accessor: 'worker_key' },
    { header: 'Kind', accessor: 'worker_kind' },
    { header: 'Transport', accessor: 'transport' },
    {
      header: 'Endpoints',
      accessor: 'endpoint',
      Cell: (_val: string | undefined, row?: WorkerRow) => {
        const endpoints = (row?.endpoints?.length ? row.endpoints : (row?.endpoint ? [row.endpoint] : []))
          .map((s) => String(s).trim())
          .filter(Boolean);
        return <span className="text-xs break-all whitespace-pre-line">{endpoints.length ? endpoints.join('\n') : '—'}</span>;
      },
    },
    { header: 'Region', accessor: 'region', Cell: (val: string | null) => val ?? '—' },
    { header: 'Created', accessor: 'created_at', Cell: (val: string) => (
      <span title={formatDate(val)} className="text-neutral-300 whitespace-nowrap">
        {timeAgo(val)}
      </span>
    ) },
    {
      header: 'Actions',
      accessor: 'worker_key',
      Cell: (_val: string | undefined, row?: WorkerRow) => {
        const workerKey = String(row?.worker_key || '').trim();
        if (!workerKey) return null;

        return (
          <ConfirmDeleteButton
            title="worker"
            confirmText={workerKey}
            onConfirm={async () => {
              await deleteMutation.mutateAsync({ worker_key: workerKey });
            }}
          />
        );
      },
    },
  ], [deleteMutation, now]);

  return (
    <div className="h-full min-h-0 flex flex-col gap-4">
      <div className="text-sm text-neutral-400">
        Registry view of orchestrator workers.
      </div>

      <div className="pac-glass rounded-md flex flex-col min-h-0 flex-1 overflow-hidden">
        <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between">
          <div className="text-sm text-neutral-200">Workers</div>
          <div className="text-xs text-neutral-500">Live registry</div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading && <div className="p-3 text-sm text-neutral-400">Loading…</div>}
          {error && (
            <div className="p-3 text-sm text-rose-400">
              Failed to load: {error.message}
            </div>
          )}

          {!isLoading && !error && (
            <MinimalisticTable<WorkerRow>
              columns={columns}
              data={workersForTable}
              noDataMessage="No workers found."
              showNewButton
              onNew={() => setOpenDialog(true)}
              showSearch
              searchPlaceholder="Search workers…"
            />
          )}
        </div>
      </div>

      <CreateWorkerDialog open={openDialog} onOpenChange={setOpenDialog} />
    </div>
  );
}
